import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Avatar,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  IconButton,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import {
  ArrowBack,
  LocationOn,
  LocalHospital,
  CalendarToday,
  AccessTime,
  Description,
  PictureAsPdf,
  Download,
  Cancel as CancelIcon,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@services/authService';
import VisitStatusBadge from '@components/receptionist/VisitStatusBadge';
import clientDashboardService from '@services/clientDashboardService';
import { visitsService } from '@services/visitsService';

interface OwnerVisitSummary {
  message?: string;
  operationalStatus?: string;
  status?: string;
  chief_complaint?: string;
  diagnosis?: string;
  treatment_plan?: string;
  recommendations?: string;
  follow_up_notes?: string;
}

const VisitDetail: React.FC = () => {
  const { visitId: appointmentId } = useParams<{ visitId: string }>();
  const navigate = useNavigate();
  const [visit, setVisit] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ownerSummary, setOwnerSummary] = useState<OwnerVisitSummary | null>(null);

  // State pentru dialogul de anulare
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const loadVisit = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/appointments/${appointmentId}`);
        const appointment = response.data.data;
        setVisit(appointment);
      } catch (err: any) {
        console.error('Error loading visit:', err);
        setError(err.response?.data?.error || 'Eroare la incarcarea detaliilor');
      } finally {
        setLoading(false);
      }
    };

    if (appointmentId) {
      loadVisit();
    }
  }, [appointmentId]);

  useEffect(() => {
    const vid = visit?.visitId || visit?.visit_id;
    if (!vid) {
      setOwnerSummary(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const s = (await visitsService.getOwnerSafeVisitSummary(String(vid))) as OwnerVisitSummary;
        if (!cancelled) setOwnerSummary(s);
      } catch {
        if (!cancelled) setOwnerSummary(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visit?.visitId, visit?.visit_id, visit?.status]);

  // Funcția de anulare programare
  const handleCancelAppointment = async () => {
    if (!visit || !cancelReason.trim()) return;

    try {
      setCancelling(true);
      await clientDashboardService.cancelAppointment(visit.id, cancelReason);
      
      // Reîncarcă datele programării pentru a actualiza statusul
      const response = await api.get(`/appointments/${appointmentId}`);
      setVisit(response.data.data);
      
      setCancelDialogOpen(false);
      setCancelReason('');
      alert('Programarea a fost anulata cu succes!');
    } catch (err: any) {
      console.error('Error cancelling appointment:', err);
      alert(err.response?.data?.error || 'Eroare la anularea programarii');
    } finally {
      setCancelling(false);
    }
  };

  // Verifică dacă programarea poate fi anulată
  const canCancelAppointment = (appointment: any) => {
    if (!appointment) return false;
    
    const appointmentDate = new Date(appointment.scheduledAt);
    const now = new Date();
    const hoursUntilAppointment = (appointmentDate.getTime() - now.getTime()) / (1000 * 60 * 60);
    
    return (
      appointment.status === 'pending_confirmation' ||
      appointment.status === 'confirmed' ||
      appointment.status === 'scheduled'
    ) && hoursUntilAppointment >= 24;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !visit) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Programarea nu a fost gasita'}</Alert>
        <Button onClick={() => navigate('/appointments')} sx={{ mt: 2 }}>
          Inapoi la programari
        </Button>
      </Container>
    );
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ro-RO', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString('ro-RO', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getAnimalEmoji = (species: string) => {
    switch (species?.toLowerCase()) {
      case 'dog': return '🐶';
      case 'cat': return '🐱';
      case 'bird': return '🐦';
      case 'rabbit': return '🐰';
      case 'horse': return '🐴';
      default: return '🐾';
    }
  };

  const isPastVisit = visit ? new Date(visit.scheduledAt) < new Date() : false;
  const canCancel = visit && !isPastVisit && (visit.status === 'pending_confirmation' || visit.status === 'confirmed');
  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/client/dashboard')}
          sx={{ cursor: 'pointer' }}
        >
          Acasa
        </Link>
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/appointments')}
          sx={{ cursor: 'pointer' }}
        >
          Vizite
        </Link>
        <Typography color="text.primary">Detalii vizita</Typography>
      </Breadcrumbs>

      {/* Back Button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/appointments')}
        sx={{ mb: 3, textTransform: 'none' }}
      >
        Inapoi la programari
      </Button>

      {/* Main Card */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.light', fontSize: '2rem' }}>
                {getAnimalEmoji(visit.animalSpecies)}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={600}>
                  {visit.animalName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {visit.animalSpecies === 'dog' ? 'Caine' : visit.animalSpecies === 'cat' ? 'Pisica' : visit.animalSpecies}
                </Typography>
              </Box>
            </Box>
            <VisitStatusBadge status={visit.status} size="medium" />
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Details Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            {/* Clinica */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocationOn sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  CLINICA
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {visit.clinicName}
              </Typography>
              <Button
                size="small"
                onClick={() => navigate(`/client/clinics/${visit.clinicId}`)}
                sx={{ textTransform: 'none', mt: 0.5, p: 0, minWidth: 'auto' }}
              >
                Vezi detalii clinica →
              </Button>
            </Box>

            {/* Veterinar */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <LocalHospital sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  VETERINAR
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                Dr. {visit.veterinarianFirstName} {visit.veterinarianLastName}
              </Typography>
            </Box>

            {/* Data */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CalendarToday sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  DATA
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {formatDate(visit.scheduledAt)}
              </Typography>
            </Box>

            {/* Ora */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <AccessTime sx={{ color: 'text.secondary' }} />
                <Typography variant="subtitle2" color="text.secondary">
                  ORA
                </Typography>
              </Box>
              <Typography variant="body1" fontWeight={600}>
                {formatTime(visit.scheduledAt)}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Motiv si Note */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Description sx={{ color: 'text.secondary' }} />
              <Typography variant="subtitle2" color="text.secondary">
                MOTIV VIZITA
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {visit.reason || 'Nespecificat'}
            </Typography>

            {visit.notes && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  NOTE SUPLIMENTARE
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {visit.notes}
                </Typography>
              </>
            )}
          </Box>

          {/* Timeline Status */}
          {visit.created_at && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                CRONOLOGIE
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      Programare creata
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(visit.created_at).toLocaleString('ro-RO')}
                    </Typography>
                  </Box>
                </Box>

                {visit.status === 'confirmed' && visit.updated_at && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Confirmata de clinica
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(visit.updated_at).toLocaleString('ro-RO')}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {visit.status === 'completed' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircle sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Vizita finalizata
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(visit.updated_at).toLocaleString('ro-RO')}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {visit.status === 'cancelled' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Programare anulata
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(visit.updated_at).toLocaleString('ro-RO')}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </>
          )}

        </CardContent>
      </Card>

      {ownerSummary && (
        <Card sx={{ borderRadius: 3, mt: 3 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Rezumat consultație (pentru dumneavoastră)
            </Typography>
            {ownerSummary.message && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {String(ownerSummary.message)}
              </Alert>
            )}
            {ownerSummary.chief_complaint != null && String(ownerSummary.chief_complaint).trim() !== '' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Motiv / prezentare
                </Typography>
                <Typography variant="body2">{String(ownerSummary.chief_complaint)}</Typography>
              </Box>
            )}
            {ownerSummary.diagnosis != null && String(ownerSummary.diagnosis).trim() !== '' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Evaluare
                </Typography>
                <Typography variant="body2">{String(ownerSummary.diagnosis)}</Typography>
              </Box>
            )}
            {ownerSummary.treatment_plan != null && String(ownerSummary.treatment_plan).trim() !== '' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Plan
                </Typography>
                <Typography variant="body2">{String(ownerSummary.treatment_plan)}</Typography>
              </Box>
            )}
            {ownerSummary.recommendations != null && String(ownerSummary.recommendations).trim() !== '' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Recomandări
                </Typography>
                <Typography variant="body2">{String(ownerSummary.recommendations)}</Typography>
              </Box>
            )}
            {ownerSummary.follow_up_notes != null && String(ownerSummary.follow_up_notes).trim() !== '' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Urmărire
                </Typography>
                <Typography variant="body2">{String(ownerSummary.follow_up_notes)}</Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Notele interne ale clinicii nu sunt afișate aici. Documentele atașate le găsiți în secțiunea Documente, când sunt disponibile.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {canCancelAppointment(visit) && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => setCancelDialogOpen(true)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Anuleaza programarea
          </Button>
        )}
      </Box>

      {/* Dialog pentru anulare programare */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Anuleaza Programarea</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Programarile pot fi anulate doar cu minimum 24 ore inainte.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Va rugam sa specificati motivul anularii:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Ex: Schimbare de planuri, urgențe personale..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Renunta
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleCancelAppointment}
            disabled={!cancelReason.trim() || cancelling}
          >
            {cancelling ? 'Se anuleaza...' : 'Confirma Anularea'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VisitDetail;

